import * as yup from "yup";

import type { BasicRoute, RouteResponseBody, RouteSchema } from "../../..";
import type { UniteHebergementDto } from "../../../dto/hebergement.dto";
import type { PostSiteResponse } from "./postSite";

export interface PatchSiteVisiteTransportBody {
  excursionDescription: string | null;
  organismeId: number;
  deplacementProximiteDescription: string | null;
  vehiculesAdaptes: boolean | null;
  unites: Array<{
    id: number;
    visiteLocaux: boolean | null;
    visiteLocauxAt: string | Date | null;
  }>;
}

export interface PatchSiteVisiteTransportResponse {
  site: PostSiteResponse | null;
  unitesHebergement: UniteHebergementDto[];
}

export interface PatchSiteVisiteTransportRoute extends BasicRoute {
  method: "PATCH";
  path: "/hebergement/site/{siteId}/visite-et-transport";
  params: { siteId: string };
  body: PatchSiteVisiteTransportBody;
  response: RouteResponseBody<PatchSiteVisiteTransportResponse>;
}

const requiredKey = <T extends yup.AnySchema>(schema: T): T =>
  schema.test(
    "required-key",
    "Champ obligatoire",
    (value) => value !== undefined,
  );

const visiteLocauxAtSchema = () =>
  yup
    .mixed<string | Date>()
    .transform((_, raw) =>
      typeof raw === "string" && raw.trim() !== "" ? new Date(raw) : raw,
    )
    .nullable()
    .test(
      "visite-locaux-future",
      "La date de visite des locaux ne peut pas être dans le futur",
      (value) =>
        value === null ||
        value === undefined ||
        new Date(value).getTime() <= Date.now(),
    );

export const PatchSiteVisiteTransportRouteSchema: RouteSchema<PatchSiteVisiteTransportRoute> =
  {
    body: yup.object({
      deplacementProximiteDescription: requiredKey(
        yup
          .string()
          .nullable()
          .max(
            2000,
            "La description du déplacement de proximité est trop longue",
          ),
      ),
      excursionDescription: requiredKey(
        yup
          .string()
          .nullable()
          .max(2000, "La description de l'excursion est trop longue"),
      ),
      organismeId: yup
        .number()
        .typeError("L'organisme doit être un nombre")
        .required("Champ obligatoire"),
      unites: yup
        .array()
        .of(
          yup.object({
            id: yup
              .number()
              .typeError("L'identifiant de l'unité doit être un nombre")
              .required("Champ obligatoire"),
            visiteLocaux: yup.boolean().nullable(),
            visiteLocauxAt: visiteLocauxAtSchema(),
          }),
        )
        .default(undefined)
        .required("Champ obligatoire"),
      vehiculesAdaptes: requiredKey(yup.boolean().nullable()),
    }) as unknown as yup.ObjectSchema<PatchSiteVisiteTransportBody>,
    params: yup.object({
      siteId: yup.string().required(),
    }),
  };
